import { gql, DocumentNode } from '@apollo/client';
import { Box, Typography, Link, Divider, Paper, List } from '@mui/material';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Spinner } from '../../Spinner';
import {
	EventCategory,
	InvoiceViewHistoryInvoiceFragment,
	SortOrderType,
	useInvoiceViewInvoice_GetInvoiceHistoryQuery,
} from '#types';
import { FlattenedUser } from '../../../types/user';
import styles from './InvoiceViewHistory.module.scss';
import { InvoiceViewHistoryItem } from './InvoiceViewHistoryItem';
import { useToast } from '../../../hooks/useToast';
import ErrorBoundary from '../../ErrorBoundary';
import rpcShared from '@rockpapercoin/rpc-shared';

const universalInvoiceHistoryEventCategories = [
	EventCategory.InstallmentPaymentFailed,
	EventCategory.InstallmentScheduledPaymentFailed,
	EventCategory.InstallmentPaymentRequestedByGuest,
	EventCategory.InstallmentPaymentRequestedByClient,
	EventCategory.InstallmentPaymentRequestedByPlanner,
	EventCategory.InstallmentPaymentRequestedBySchedule,
	EventCategory.InstallmentPaymentUpdatedOfflineByVendor,
	EventCategory.InstallmentPaymentRecordedOfflineByVendor,
	EventCategory.OfflineInstallmentPaymentUnRecordedByVendor,
	EventCategory.OfflineInstallmentRefundUnRecordedByVendor,
	EventCategory.InstallmentRefundFailed,
	EventCategory.InstallmentRefundRequested,
	EventCategory.InstallmentScheduledPaymentFailed,
	EventCategory.VendorDisputeOpened,
	EventCategory.VendorDisputeWonByClient,
	EventCategory.VendorDisputeWonByVendor,
];

const customerInvoiceHistoryEventCategories = [
	...universalInvoiceHistoryEventCategories,
	EventCategory.InvoicePaymentsDescheduledByFailure,
	EventCategory.InvoicePaymentsDescheduledByPlanner,
	EventCategory.InvoicePaymentsDescheduledByClient,
	EventCategory.InvoicePaymentsDescheduledByVendorAction,
	EventCategory.InstallmentPaymentRescheduledByFailure,
	EventCategory.InstallmentPaymentRescheduledByVendor,
	EventCategory.InvoicePaymentsScheduledByPlanner,
	EventCategory.InvoicePaymentsScheduledByClient,
];

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getInvoiceHistory = gql`
	query invoiceViewInvoice_getInvoiceHistory(
		$where: EventWhereInput
		$orderBy: [EventOrderByInput]
	) {
		getEventsWhere(where: $where, orderBy: $orderBy) {
			...invoiceViewHistoryItemEvent
		}
	}
	${ InvoiceViewHistoryItem.fragments.invoiceViewHistoryItemEventFragment }
`;

const invoiceViewHistoryInvoiceFragment = gql`
	fragment invoiceViewHistoryInvoice on Invoice {
		id
		contact {
			...getViewingUserDocumentRelationshipContact
		}
		organization {
			...invoiceViewHistoryItemInvoiceOrganization
		}
		...invoiceViewHistoryItemInvoice
	}
	${ InvoiceViewHistoryItem.fragments
		.invoiceViewHistoryItemInvoiceOrganizationFragment }
	${ InvoiceViewHistoryItem.fragments.invoiceViewHistoryItemInvoiceFragment }
	${ rpcShared.contacts.getViewingUserDocumentRelationshipContactFragment }
`;

export type InvoiceViewHistoryProps = {
	invoice?: InvoiceViewHistoryInvoiceFragment;
	requestingUserBaseId?: string;
};

type InvoiceViewHistoryPropsWithFragment = React.FC<InvoiceViewHistoryProps> & {
	fragments: {
		invoiceViewHistoryInvoiceFragment: DocumentNode;
	};
};

export const InvoiceViewHistory: InvoiceViewHistoryPropsWithFragment = ( {
	invoice,
	requestingUserBaseId,
} ) => {
	const user: FlattenedUser = useSelector( ( state: any ) => state.user );
	const { showError } = useToast();

	const { viewingUserIsCustomer, viewingUserIsAPPA } =
		rpcShared.contacts.getViewingUserDocumentRelationship( {
			contact: invoice?.contact,
			viewingUserId: requestingUserBaseId,
		} );
	const viewingUserIsCustomerOrAPPA =
		viewingUserIsCustomer || viewingUserIsAPPA;

	const { data, error, loading } = useInvoiceViewInvoice_GetInvoiceHistoryQuery(
		{
			variables: {
				where: {
					...( invoice?.id ? { invoice: { id: invoice.id } } : {} ),
					category: {
						in: viewingUserIsCustomerOrAPPA
							? customerInvoiceHistoryEventCategories
							: universalInvoiceHistoryEventCategories,
					},
				},
				orderBy: [ { createdAt: SortOrderType.desc } ],
			},
			skip: !invoice?.id || !user?.isLoggedIn,
		}
	);

	useEffect( () => {
		if ( error ) {
			showError(
				'Oops! Something went wrong loading your invoice history. Please let us know.'
			);
		}
	}, [ error, showError ] );

	return (
		<Paper className={ styles.container } variant='outlined'>
			{ loading ? (
				<Spinner />
			) : (
				<Box className={ styles.main }>
					<Typography variant='h6'>History</Typography>
					{ user?.isLoggedIn ? (
						<List>
							{ data?.getEventsWhere
								?.filter(
									( event ) =>
										!event.category.includes( 'Installment' ) ||
										( event.paymentInstallments != null &&
											event.paymentInstallments.length > 0 )
								)
								.map( ( event, index, filteredEvents ) => (
									<ErrorBoundary key={ index } extraScope={ { event } }>
										<InvoiceViewHistoryItem
											event={ event }
											invoice={ invoice }
											key={ index }
											requestingUserBaseId={ requestingUserBaseId }
										/>
										{ index !== filteredEvents.length - 1 ? <Divider /> : null }
									</ErrorBoundary>
								) ) }
						</List>
					) : (
						<Box className={ styles.signUp }>
							<Typography>
								<Link className={ styles.link } href='/SignUp'>
									Sign up today
								</Link>
								{ 'to view your transaction history.' }
							</Typography>
						</Box>
					) }
				</Box>
			) }
		</Paper>
	);
};

InvoiceViewHistory.fragments = {
	invoiceViewHistoryInvoiceFragment,
};
